/* Single source of truth for the stock-locator vehicle list.
   Drives the homepage stock section AND the nav `stock` menu, so adding
   a new tracked car is a one-line change here (the per-car /stock/<slug>
   route + its map component still have to exist — see app/stock/<slug>).

   Pure data, no React / no `server-only`: imported by both the server
   homepage (app/_components/home/HomeBoard.tsx) and the client nav menu
   (StockMenu.tsx). */

export type StockGroupId =
  | 'honda'
  | 'gr-garage'
  | 'dad-cars'
  | 'subaru'
  | 'gm'
  | 'nissan'
  | 'hyundai'
  | 'mazda';

export interface StockVehicle {
  /** Route slug → `/stock/<slug>` (must match an app/stock/<slug> route). */
  slug: string;
  /** Inventory API key → `/data/<invModel>` (see app/stock/StockTemplate). */
  invModel: string;
  /** Display label — homepage cards + desktop menu. */
  label: string;
  /** Lowercase nav-style label, matching the terse tab voice. */
  navLabel: string;
  /** Analytics event suffix (`home_stock_<event>`, `nav_stock_<event>`). */
  event: string;
  group: StockGroupId;
  /** Optional house-style illustration (public/cars/<family>/<gen>.png, the
      same left-facing art /compare + /cars use). Cream-backgrounded, so the
      homepage card it renders on is cream too. */
  art?: string;
  /** Override the card's background-size for `art` (default 'contain', i.e.
      fit to card height). Use e.g. 'auto 80%' to shrink a car that fills more
      of its source frame than the others. */
  artSize?: string;
  /** Small nav-menu thumbnail (public/stock-thumbs/<slug>.png), generated from
      `art` by scripts/gen-stock-thumbs.sh. */
  thumb?: string;
}

export interface StockGroup {
  id: StockGroupId;
  /** Title-case group label (homepage). */
  label: string;
  /** Lowercase group label (nav menu headers). */
  navLabel: string;
}

/* Integra Type S is an Acura, but it sits with the Civic Type R as the
   Honda-family hot hatch pair — hence "Honda / Acura". Corvette + Blackwing
   pair up the same way under the GM umbrella — "Chevrolet / Cadillac".
   "GR Garage" is the GR-badge group (the onboarding gate). "Dad Cars" holds
   non-GR Toyotas (RAV4 Woodland + Crown Signia).

   ORDER MATTERS: this group order (busiest makes first) and the vehicle order
   within each group drive BOTH the stock menu and the homepage board, so the
   two always match. The homepage lays groups out four-across on desktop; the
   current sizes (2·2 / 4 / 2·2 / 1·1·2) fill every row — when adding a car,
   check the homepage grid still packs. */
export const STOCK_GROUPS: StockGroup[] = [
  { id: 'honda', label: 'Honda / Acura', navLabel: 'honda · acura' },
  { id: 'gm', label: 'Chevrolet / Cadillac', navLabel: 'chevrolet · cadillac' },
  { id: 'gr-garage', label: 'GR Garage', navLabel: 'gr garage' },
  { id: 'subaru', label: 'Subaru', navLabel: 'subaru' },
  { id: 'hyundai', label: 'Hyundai', navLabel: 'hyundai' },
  { id: 'nissan', label: 'Nissan', navLabel: 'nissan' },
  { id: 'mazda', label: 'Mazda', navLabel: 'mazda' },
  { id: 'dad-cars', label: 'Dad Cars', navLabel: 'dad cars' },
];

export const STOCK_VEHICLES: StockVehicle[] = [
  // artSize per car normalises the on-card car height to ≈55% (each source
  // image frames its car at a different scale — measured fill ratios drive
  // these values; see the measurement note in the brainstorm thread).
  // `thumb` is a small (≈96px-wide) variant generated by scripts/gen-stock-thumbs.sh.
  { slug: 'civic-type-r', invModel: 'ctr', label: 'Civic Type R', navLabel: 'civic type r', event: 'ctr', group: 'honda', art: 'https://assets.motoringobsession.com/cars/civic-type-r/fl5.png', artSize: 'auto 77%', thumb: '/stock-thumbs/civic-type-r.png' },
  { slug: 'integra-type-s', invModel: 'its', label: 'Integra Type S', navLabel: 'integra type s', event: 'its', group: 'honda', art: 'https://assets.motoringobsession.com/cars/integra-type-s/de5.png', artSize: 'auto 85%', thumb: '/stock-thumbs/integra-type-s.png' },
  { slug: 'gr86', invModel: 'gr86', label: 'GR86', navLabel: 'gr86', event: 'gr86', group: 'gr-garage', art: 'https://assets.motoringobsession.com/cars/gr86/zn8.png', artSize: 'auto 89%', thumb: '/stock-thumbs/gr86.png' },
  { slug: 'gr-corolla', invModel: 'gr-corolla', label: 'GR Corolla', navLabel: 'gr corolla', event: 'gr_corolla', group: 'gr-garage', art: 'https://assets.motoringobsession.com/cars/gr-corolla/e210.png', artSize: 'auto 74%', thumb: '/stock-thumbs/gr-corolla.png' },
  { slug: 'gr-supra', invModel: 'gr-supra', label: 'GR Supra', navLabel: 'gr supra', event: 'gr_supra', group: 'gr-garage', art: 'https://assets.motoringobsession.com/cars/supra/a90.png', artSize: 'auto 78%', thumb: '/stock-thumbs/gr-supra.png' },
  { slug: 'rav4-phev-gr-sport', invModel: 'rav4phev', label: 'RAV4 PHEV GR Sport', navLabel: 'rav4 phev gr sport', event: 'rav4phev', group: 'gr-garage', art: 'https://assets.motoringobsession.com/cars/rav4-phev-gr-sport/2026.png', artSize: 'auto 72%', thumb: '/stock-thumbs/rav4-phev-gr-sport.png' },
  { slug: 'rav4', invModel: 'rav4', label: 'RAV4 Woodland', navLabel: 'rav4 woodland', event: 'rav4', group: 'dad-cars', art: 'https://assets.motoringobsession.com/cars/rav4-woodland/2026.png', artSize: 'auto 69%', thumb: '/stock-thumbs/rav4.png' },
  { slug: 'crown-signia', invModel: 'crown-signia', label: 'Crown Signia', navLabel: 'crown signia', event: 'crown_signia', group: 'dad-cars', art: 'https://assets.motoringobsession.com/cars/crown-signia/2026.png', artSize: 'auto 66%', thumb: '/stock-thumbs/crown-signia.png' },
  // Subaru — BRZ (GR86 twin) + WRX. Art from /cars (brz/zd8, wrx/vb).
  { slug: 'brz', invModel: 'brz', label: 'BRZ', navLabel: 'brz', event: 'brz', group: 'subaru', art: 'https://assets.motoringobsession.com/cars/brz/zd8.png', artSize: 'auto 79%', thumb: '/stock-thumbs/brz.png' },
  { slug: 'wrx', invModel: 'wrx', label: 'WRX', navLabel: 'wrx', event: 'wrx', group: 'subaru', art: 'https://assets.motoringobsession.com/cars/wrx/vb.png', artSize: 'auto 79%', thumb: '/stock-thumbs/wrx.png' },
  // Chevrolet / Cadillac — Corvette (all C8 series: Stingray/GS/E-Ray/Z06/
  // ZR1/ZR1X) + V-Series Blackwing (CT5-V + CT4-V, one MTR model `blackwing`).
  { slug: 'corvette', invModel: 'corvette', label: 'Corvette', navLabel: 'corvette', event: 'corvette', group: 'gm', art: 'https://assets.motoringobsession.com/cars/corvette/c8.png', artSize: 'auto 75%', thumb: '/stock-thumbs/corvette.png' },
  { slug: 'blackwing', invModel: 'blackwing', label: 'Blackwing', navLabel: 'blackwing', event: 'blackwing', group: 'gm', art: 'https://assets.motoringobsession.com/cars/ct5-v-blackwing/gen1.png', artSize: 'auto 70%', thumb: '/stock-thumbs/blackwing.png' },
  // Nissan — Z (RZ34: Sport/Performance/NISMO). Route slug is `nissanz` (a bare
  // `/stock/z` reads poorly); the MTR model slug stays `z` via invModel.
  { slug: 'nissanz', invModel: 'z', label: 'Nissan Z', navLabel: 'nissan z', event: 'nissanz', group: 'nissan', art: 'https://assets.motoringobsession.com/cars/nissan-z/rz34.png', artSize: 'auto 78%', thumb: '/stock-thumbs/nissanz.png' },
  // Hyundai N — Elantra N + Ioniq 5 N (two separate MTR models, one nav group).
  { slug: 'elantran', invModel: 'elantra-n', label: 'Elantra N', navLabel: 'elantra n', event: 'elantran', group: 'hyundai', art: 'https://assets.motoringobsession.com/cars/elantra-n/cn7.png', artSize: 'auto 74%', thumb: '/stock-thumbs/elantran.png' },
  { slug: 'ioniq5n', invModel: 'ioniq-5-n', label: 'Ioniq 5 N', navLabel: 'ioniq 5 n', event: 'ioniq5n', group: 'hyundai', art: 'https://assets.motoringobsession.com/cars/ioniq-5-n/gen1.png', artSize: 'auto 70%', thumb: '/stock-thumbs/ioniq5n.png' },
  // Mazda — MX-5 (Soft Top + RF, one MTR model `mx-5`). Route slug `miata`.
  { slug: 'miata', invModel: 'mx-5', label: 'MX-5 Miata', navLabel: 'mx-5 miata', event: 'miata', group: 'mazda', art: 'https://assets.motoringobsession.com/cars/miata/nd.png', artSize: 'auto 76%', thumb: '/stock-thumbs/miata.png' },
];

/** Vehicles bucketed by group, preserving STOCK_GROUPS order. */
export function stockByGroup(): { group: StockGroup; vehicles: StockVehicle[] }[] {
  return STOCK_GROUPS.map((group) => ({
    group,
    vehicles: STOCK_VEHICLES.filter((v) => v.group === group.id),
  }));
}
